import { Poll, PollQuestion } from '../interfaces/Poll';
import { PollDispatch, PollQuestionDispatch } from '../interfaces/Reducers';
import { sortByAttribute } from '../services/sort';

interface StatePoll {
    poll: Poll
    modified: boolean
}

export const pollReducer = (state: StatePoll, action: PollDispatch): StatePoll => {

    const poll = { ...state.poll };

    if (action.type === 'UPDATE_POLL') return { poll: { ...poll, ...action.payload }, modified: false };

    if (action.type === 'ADD_NAME') poll.name = action.payload;

    if (action.type === 'ADD_DESCRIPTION') poll.description = action.payload;

    if (action.type === 'HAS_TABLE' && typeof action.payload === 'boolean') poll.has_table = action.payload;

    if (action.type === 'SHOW_IN_CHARTS' && typeof action.payload === 'boolean') {
        poll.show_in_charts = action.payload
    }

    if (action.type === 'SET_AUTOMATIC_CALL' && typeof action.payload === 'boolean') poll.auto_call_enabled = action.payload;

    if (action.type === 'ADD_PROFILE' && typeof action.payload === 'number')
        poll.profile_ids = Array.isArray(poll.profile_ids) ?
            poll.profile_ids.includes(action.payload) ?
                poll.profile_ids.filter(id => id !== action.payload) : [...poll.profile_ids, action.payload]
            : [action.payload];

    if (action.type === 'CHANGE_VALIDITY_DURATION' && typeof action.payload === 'number') {
        poll.validity_duration_hours = action.payload;
    }

    if (action.type === 'SET_PROFILES' && Array.isArray(action.payload))
        poll.profile_ids = Array.isArray(poll.profile_ids) ?
            action.payload.every(id => poll.profile_ids?.includes(id)) ? [] : action.payload : action.payload;

    if (action.type === 'ADD_FREQUENCY') {
        poll.frecuency = action.payload.frecuency;
        poll.frequency_duration = action.payload.frequency_duration;
        poll.monthly_frequency_day = action.payload.monthly_frequency_day;
        poll.semanal_frequency = action.payload.semanal_frequency;
    }

    if (action.type === 'SWAP_QUESTIONS_ORDER' && Array.isArray(poll.questions)) {
        const sortedQuestions = sortByAttribute(poll.questions, 'order');

        const { indexA, indexB } = action.payload;

        const asc = indexA < indexB;
        const newQuestions = [...sortedQuestions];

        let oldOrder = newQuestions[indexA].order;
        newQuestions[indexA] = { ...newQuestions[indexA], order: newQuestions[indexB].order };
        for (let i = indexA + (asc ? 1 : -1); asc ? i <= indexB : i >= indexB; i += (asc ? 1 : -1)) {
            const orderAux = newQuestions[i].order;
            newQuestions[i] = { ...newQuestions[i], order: oldOrder };
            oldOrder = orderAux;
        }

        poll.questions = newQuestions;
    }

    if(action.type === 'CHANGE_TYPE' && typeof action.payload === 'boolean'){
        poll.is_profesional_inquiry = action.payload;
    }

    if (action.type === 'ADD_REMINDER_TIME' && typeof action.payload === 'string')
        poll.reminder_time = action.payload;

    if (action.type === 'ADD_QUESTION' && Array.isArray(poll.questions))
        poll.questions = [...poll.questions, { ...action.payload, uuid: crypto.randomUUID() }];

    if (action.type === 'EDIT_QUESTION' && Array.isArray(poll.questions)) {
        const questions = [...poll.questions];
        questions[action.payload.index] = action.payload.question;
        poll.questions = questions;
    }
    if (action.type === 'DELETE_QUESTION' && Array.isArray(poll.questions)) {
        const questions = [...poll.questions];
        questions.splice(action.payload, 1);
        poll.questions = questions.map((q, i) => ({ ...q, order: i }));
    }

    if (action.type === 'ADD_ALERTS' && Array.isArray(action.payload)) {
        poll.inquiry_alerts = action.payload;
        return { poll, modified: state.modified };
    }

    if (action.type === 'CHANGE_TYPE') poll.is_profesional_inquiry = action.payload;

    if (action.type === 'CHANGE_MODIFY') return { poll, modified: action.payload };

    return { poll, modified: true };
};


export const questionReducer = (state: PollQuestion, action: PollQuestionDispatch): PollQuestion => {
    if (action.type === 'SET_QUESTION_TYPE' && typeof action.payload === 'number') {
        const answers = (action.payload === 3 ? DEFAULT_FREE_TEXT : action.payload === 1 ? DEFAULT_ANSWERS_MULTI_OPTION : [])
            ?.map(answer => ({ ...answer, uuid: crypto.randomUUID() }));
        return { ...state, inquiry_question_type_id: action.payload, answers };
    }
    if (action.type === 'ADD_DESCRIPTION' && typeof action.payload === 'string')
        return { ...state, description: action.payload };
    if (action.type === 'ADD_PARAMETER_DESCRIPTION' && typeof action.payload === 'string')
        return { ...state, parameter_description: action.payload.trim() === '' ? null : action.payload };
    if (action.type === 'TOGGLE_REQUIRED' && typeof action.payload === 'boolean')
        return { ...state, required: action.payload };
    if (action.type === 'SET_TABLE_DESCRIPTION') {
        const answers = [...state.answers].map(answer => {
            if (answer.uuid !== action.payload.uuid) return answer;
            return { ...answer, table_description: action.payload.table_description };
        });
        return { ...state, answers };
    }
    if (action.type === 'ADD_ANSWER' && Array.isArray(state.answers))
        return { ...state, answers: [...state.answers, { ...DEFAULT_ANSWER, order: state.answers.length, uuid: crypto.randomUUID() }] };
    if (action.type === 'EDIT_ANSWER' && typeof action.payload.uuid === 'string' && typeof action.payload.answer === 'object' && Array.isArray(state.answers)) {
        const answers = [...state.answers].map(answer => {
            if (answer.uuid !== action.payload.uuid) return answer;
            return { ...answer, ...action.payload.answer };
        });
        return { ...state, answers };
    }
    if (action.type === 'DELETE_ANSWER' && Array.isArray(state.answers)) {
        const answers = state.answers.filter(answer => answer.uuid !== action.payload);
        const newAnwers = answers.map((answer, index) => ({ ...answer, order: index }));
        return { ...state, answers: newAnwers };
    }

    return state;
};


const DEFAULT_ANSWER = {
    description: '',
    color_alert: '#fff',
    value: null,
    emoji: null
};


const DEFAULT_ANSWERS_MULTI_OPTION = [{ ...DEFAULT_ANSWER, order: 0 }, { ...DEFAULT_ANSWER, order: 1 }];

const DEFAULT_FREE_TEXT = [{
    description: '',
    emoji: null,
    order: 0,
    value: null,
}];

export const DEFAULT_QUESTION = {
    inquiry_question_type_id: 1,
    description: '',
    required: true,
    answers: DEFAULT_ANSWERS_MULTI_OPTION,
};